import React from "react";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {TemplateTriggerReference} from "../../apis/requests/templates/types";
import {TriggerInteractionType} from "../../apis/requests/triggers/types";
import {openInNewTab} from "../../components/ResourcePanel";
import {triggerUrl} from "../triggers/helpers";
import {InteractionChip, TriggerStatusChip} from "../triggers/TriggerBadges";
import {TIMING_DISPLAY} from "../triggers/triggerDisplay";

export const TriggerReferenceRow = ({reference}: {reference: TemplateTriggerReference}) => (
  <Box
    component="button"
    type="button"
    onClick={() => openInNewTab(triggerUrl(reference.trigger_id))}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      width: "100%",
      p: "12px 14px",
      border: `1px solid ${Colors.blue._200}`,
      borderRadius: "8px",
      backgroundColor: Colors.bw.white,
      fontFamily: "inherit",
      textAlign: "left",
      cursor: "pointer",
      "&:hover": {borderColor: Colors.blue._300},
      "&:hover .trigger-go": {backgroundColor: Colors.blue._800, color: Colors.bw.white},
    }}
  >
    <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.6}}>
      <Text size={14} variant="inter600" sx={{color: Colors.grey._900}}>
        {reference.trigger_name}
        <Box component="span" sx={{color: Colors.grey._400, fontWeight: 400}}>
          {" / "}
        </Box>
        priority {reference.priority} — {reference.condition_name}
      </Text>
      <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap"}}>
        <InteractionChip interactionType={reference.interaction_type as TriggerInteractionType} />
        <Text size={12} sx={{color: Colors.grey._600}}>
          {reference.slot === "email" ? "Email" : "Text"} slot ·{" "}
          {TIMING_DISPLAY[reference.timing].shortLabel.toLowerCase()}
        </Text>
        {reference.trigger_status !== "active" && <TriggerStatusChip status={reference.trigger_status} />}
      </Box>
    </Box>
    <Box
      className="trigger-go"
      sx={{
        flexShrink: 0,
        width: 26,
        height: 26,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: Colors.blue._100,
        color: Colors.blue._800,
      }}
    >
      <ChevronRightIcon sx={{fontSize: 18}} />
    </Box>
  </Box>
);

const TemplateTriggeredByPanel = ({triggers}: {triggers?: TemplateTriggerReference[]}) => {
  if (!triggers?.length) return null;
  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: "16px 18px",
        border: `1px solid ${Colors.blue._200}`,
        borderRadius: "8px",
        backgroundColor: Colors.blue._100,
      }}
    >
      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <BoltOutlinedIcon sx={{fontSize: 18, color: Colors.blue._800}} />
        <Text size={14} variant="inter600" sx={{color: Colors.blue._900}}>
          Triggered by
        </Text>
      </Box>
      {triggers.map((reference) => (
        <TriggerReferenceRow
          key={`${reference.trigger_id}-${reference.priority}-${reference.slot}`}
          reference={reference}
        />
      ))}
    </Box>
  );
};

export default TemplateTriggeredByPanel;
